/**
 * 내 세션 (권한·구독)
 * ---------------------------------------------------------------------------
 * 제품 API 의 /session 을 한 번 불러 화면 구성에 쓴다.
 *
 * 여기서 감추는 메뉴는 **UX 일 뿐 보안이 아니다.** 실제 판정은 서버 가드가
 * 하며, 화면을 우회해 API 를 직접 불러도 같은 결과가 나온다.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/**
 * 지금 값을 등록·수정할 수 있는가. 플랫폼이 판정해서 내려 준다.
 *
 * `writable: false` 여도 **조회는 된다.** 운영일지는 법정 기록이라 이미 쓴 것을
 * 못 보게 하면 고객이 법정 의무를 이행할 수 없다.
 */
export interface EcoAccess {
  readable: boolean;
  writable: boolean;
  reason: 'TRIAL_EXPIRED' | 'NO_SUBSCRIPTION' | 'PAST_DUE' | 'SUSPENDED' | null;
  /** 화면에 그대로 띄울 문구 */
  message: string | null;
  trialEndsAt: string | null;
  trialDaysLeft: number | null;
}

/**
 * 기업 기본 정보. 플랫폼(WORK&Hub)이 내려 준다.
 *
 * 사업장 등록 화면이 기본값으로 쓴다 — 사업장은 보통 회사와 상호·사업자번호·
 * 주소가 같아서, 미리 채워 두면 같은 값을 두 번 적지 않아도 된다.
 * 고칠 수 있는 값이다. 사업장이 본사와 다르면 그 자리에서 바꾸면 된다.
 */
export interface EcoCompany {
  companyName: string;
  ceoName: string | null;
  bizRegNo: string | null;
  bizType: string | null;
  bizItem: string | null;
  zipCode: string | null;
  address1: string | null;
  address2: string | null;
  companyTel: string | null;
}

export interface EcoSession {
  tenantId: string;
  userId: string;
  displayName: string | null;
  companyName: string | null;
  company: EcoCompany | null;
  /**
   * 기업 로고의 지문. 등록하지 않았으면 null.
   *
   * 그림이 아니다 — 머리말이 이 값을 보고 `GET /company/logo` 를 한 번
   * 부른다. 로고의 원본은 WORK&Hub 이고, 거기서 바꾸면 여기에도 반영된다.
   */
  companyLogo: { etag: string; contentType: string; updatedAt: string } | null;
  roles: string[];
  permissions: string[];
  modules: string[];
  supportMode: boolean;
  platformWebUrl: string;
  access: EcoAccess;
}

export function useSession() {
  return useQuery<EcoSession>({
    queryKey: ['session'],
    queryFn: async () => (await api.get<EcoSession>('/session')).data,
    // 권한은 자주 바뀌지 않는다. 화면을 옮길 때마다 다시 부를 이유가 없다.
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * 지금 등록·수정할 수 있는가.
 *
 * 세션을 아직 못 받았으면 **열린 것으로 본다.** 저장 버튼이 깜빡 잠기는 것보다
 * 나은 선택이다 — 진짜 판정은 서버 가드가 하므로, 잠기지 않은 버튼을 눌러도
 * 막혀야 할 요청은 막힌다.
 */
export function canWrite(session: EcoSession | undefined): boolean {
  return session?.access?.writable ?? true;
}

/** 플랫폼과 같은 와일드카드 규칙. 'eco.site:*' 는 'eco.site:read' 를 포함한다. */
export function can(session: EcoSession | undefined, required: string): boolean {
  if (!session) return false;
  return session.permissions.some((granted) => matches(granted, required));
}

function matches(granted: string, required: string): boolean {
  if (granted === required) return true;

  const [gScope, gAction] = granted.split(':');
  const [rScope, rAction] = required.split(':');
  if (!gScope || !rScope) return false;

  const [gProduct, gResource] = gScope.split('.');
  const [rProduct, rResource] = rScope.split('.');

  const scopeOk = gScope === rScope || (gProduct === rProduct && gResource === '*');
  return scopeOk && (gAction === '*' || gAction === rAction);
}
